/**
 * AnalysisResults Component
 * 
 * Analysis results display UI component.
 * Takes result prop from HomePage.js, only handles display.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { getDefaultCurrency } from "@/utils/currency";
import { useState } from "react";
import {
  formatNumberRuleD,
  formatFollowerCount,
  formatPercent,
  formatRange,
  calculateAverageFromRange,
} from "@/utils/formatNumber";
import { translateEval } from "@/utils/evalLabels";  // eval-ceviri tek-kaynak (inactive/very_low dahil; EN-modu TR sizinti fix)

// Helper functions
const formatCurrency = (num) => {
  if (!num && num !== 0) return '-';
  return Number(num).toLocaleString('en-US');
};

const formatDecimal = (num, decimals = 2) => {
  if (!num && num !== 0) return '-';
  return Number(num).toFixed(decimals);
};

// formatNumberShort & formatEngagementRange moved to @/utils/formatNumber (Rule D)

// =============================================
// EVALUATION LABEL TRANSLATOR
// Translates Turkish backend labels to current locale
// =============================================
// Translate frequency explanations
const translateFrequencyExplanation = (value, locale) => {
  if (!value) return value;
  
  const explanationMap = {
    // Posted X Reels in last Y days pattern
    'Son': {
      pattern: /Son (\d+) günde (\d+) Reel paylaştı/,
      translations: {
        en: (days, reels) => `Posted ${reels} Reels in the last ${days} days`,
        tr: (days, reels) => `Son ${days} günde ${reels} Reel paylaştı`,
        ar: (days, reels) => `قام بنشر ${reels} ريلز في آخر ${days} يوم`,
        fr: (days, reels) => `A posté ${reels} Reels au cours des ${days} derniers jours`,
        de: (days, reels) => `Hat ${reels} Reels in den letzten ${days} Tagen gepostet`,
        es: (days, reels) => `Publicó ${reels} Reels en los últimos ${days} días`
      }
    },
    // Weekly frequency explanations
    'Haftada en az 2': {
      en: 'At least 2 posts per week', tr: 'Haftada en az 2 paylaşım',
      ar: 'مشاركتان على الأقل أسبوعياً', fr: 'Au moins 2 posts par semaine',
      de: 'Mind. 2 Posts pro Woche', es: 'Al menos 2 posts por semana'
    },
    'Haftada 1-2': {
      en: '1-2 posts per week', tr: 'Haftada 1-2 paylaşım',
      ar: '1-2 مشاركة أسبوعياً', fr: '1-2 posts par semaine',
      de: '1-2 Posts pro Woche', es: '1-2 posts por semana'
    },
    'Haftada yaklaşık 1': {
      en: 'Approx. 1 post per week', tr: 'Haftada yaklaşık 1 paylaşım',
      ar: 'مشاركة واحدة أسبوعياً تقريباً', fr: 'Environ 1 post par semaine',
      de: 'Ca. 1 Post pro Woche', es: 'Aprox. 1 post por semana'
    },
    'Son 3 ayda': {
      en: 'No reels in the last 3 months', tr: 'Son 3 ayda reel yok',
      ar: 'لا توجد ريلز في آخر 3 أشهر', fr: 'Aucun reel au cours des 3 derniers mois',
      de: 'Keine Reels in den letzten 3 Monaten', es: 'Sin reels en los últimos 3 meses'
    },
    // Engagement segment descriptions
    'Bu takipçi segmenti için ortalama etkileşim oranı': {
      pattern: /Bu takipçi segmenti için ortalama etkileşim oranı: %(.+) - %(.+)/,
      translations: {
        en: (low, high) => `Average engagement rate for this follower segment: ${low}% - ${high}%`,
        tr: (low, high) => `Bu takipçi segmenti için ortalama etkileşim oranı: ${low}% - ${high}%`,
        ar: (low, high) => `متوسط معدل التفاعل لهذه الشريحة من المتابعين: ${low}% - ${high}%`,
        fr: (low, high) => `Taux d'engagement moyen pour ce segment d'abonnés: ${low}% - ${high}%`,
        de: (low, high) => `Durchschnittliche Engagement-Rate für dieses Follower-Segment: ${low}% - ${high}%`,
        es: (low, high) => `Tasa de interacción promedio para este segmento de seguidores: ${low}% - ${high}%`
      }
    },
    // Visibility segment descriptions (gorunurluk benchmark - ER ile simetrik)
    'Bu takipçi segmenti için ortalama izlenme': {
      pattern: /Bu takipçi segmenti için ortalama izlenme: (.+) - (.+)/,
      translations: {
        en: (low, high) => `Average views for this follower segment: ${low} - ${high}`,
        tr: (low, high) => `Bu takipçi segmenti için ortalama izlenme: ${low} - ${high}`,
        ar: (low, high) => `متوسط المشاهدات لهذه الشريحة من المتابعين: ${low} - ${high}`,
        fr: (low, high) => `Vues moyennes pour ce segment d'abonnés: ${low} - ${high}`,
        de: (low, high) => `Durchschnittliche Aufrufe für dieses Follower-Segment: ${low} - ${high}`,
        es: (low, high) => `Vistas promedio para este segmento de seguidores: ${low} - ${high}`
      }
    }
  };
  
  // Check "Son X günde Y Reel paylaştı" pattern
  const reelPattern = /Son (\d+) günde (\d+) Reel paylaş/;
  const reelMatch = value.match(reelPattern);
  if (reelMatch) {
    const days = reelMatch[1];
    const reels = reelMatch[2];
    const translations = explanationMap['Son'].translations;
    return translations[locale] ? translations[locale](days, reels) : translations.en(days, reels);
  }
  
  // Check engagement segment pattern
  const engPattern = /Bu takipçi segmenti için ortalama etkileşim oranı: %(.+) - %(.+)/;
  const engMatch = value.match(engPattern);
  if (engMatch) {
    const low = engMatch[1];
    const high = engMatch[2];
    const translations = explanationMap['Bu takipçi segmenti için ortalama etkileşim oranı'].translations;
    return translations[locale] ? translations[locale](low, high) : translations.en(low, high);
  }
  
  // Check visibility segment pattern
  const visPattern = /Bu takipçi segmenti için ortalama izlenme: (.+) - (.+)/;
  const visMatch = value.match(visPattern);
  if (visMatch) {
    const low = visMatch[1];
    const high = visMatch[2];
    const translations = explanationMap['Bu takipçi segmenti için ortalama izlenme'].translations;
    return translations[locale] ? translations[locale](low, high) : translations.en(low, high);
  }
  
  // Check for direct key matches
  for (const [key, translations] of Object.entries(explanationMap)) {
    if (typeof translations === 'object' && !translations.pattern && value.includes(key)) {
      return translations[locale] || translations.en || value;
    }
  }
  
  return value;
};

// Benchmark satiri: i18n string'i ": " noktasindan render-split -> referans (b/e) iki-tier yapisi:
// label kucuk-soluk (text-xs opacity-80) + sayi bold (font-bold). Ayrac yoksa tum metin tek span (guard).
// METIN DEGISMEZ (sadece render-split); renk dis className'den (getColorClass tema mirasi) gelir.
const BenchmarkLine = ({ text, className }) => {
  const safe = text || '';
  const idx = safe.indexOf(': ');
  return (
    <p className={className}>
      {idx === -1 ? (
        <span>{safe}</span>
      ) : (
        <>
          <span className="text-xs opacity-80">{safe.slice(0, idx + 1)}</span>{' '}
          <span className="font-bold">{safe.slice(idx + 2)}</span>
        </>
      )}
    </p>
  );
};

// Sub-component: Profile Info Card
// v13.7: Full null-safety with optional chaining
const ProfileInfoCard = ({ result, t, currency }) => {
  // v13.7 CRASH DEFENSE: Safely extract and parse values
  const username = result?.username || 'Unknown';
  const profilePic = result?.profile_pic_url;
  const [avatarFailed, setAvatarFailed] = useState(false); // v30.80: avatar onError fallback artik state+JSX (imperative DOM mutasyonu kaldirildi)
  
  // v16.5 DEBUG: Log profile pic
  console.log('[ProfileInfoCard] profile_pic_url:', profilePic, 'raw result.profile:', result?.profile);

  // v30.j: Brand partnership data consolidated into Profile card (Box 6 removed)
  const paidPartnerships = result?.paid_partnerships_count || 0;
  const collaborations = result?.collaborations_by_reel;
  const brandMentions = result?.brand_mentions;

  // Build brand chip list - REEL-BASED (Secenek 1): ayni marka N reel'de gecince N chip,
  // her biri KENDI reel'ine linkli. collaborations_by_reel backend'de reel_position'a gore
  // SIRALI + reel-ici exact-dedup'li (opara_core_v4 set) -> reel-ici cift chip riski yok.
  // (Eski marka-merkezli dedup + first-shortcode KALDIRILDI: digger reel linkleri kaybolmasin.)
  let uniqueBrands = [];
  if (collaborations && Array.isArray(collaborations) && collaborations.length > 0) {
    collaborations.forEach(collab => {
      if (collab?.brands && Array.isArray(collab.brands)) {
        collab.brands.forEach(brand => {
          if (brand) {
            uniqueBrands.push({ name: brand, shortcode: collab.shortcode || null });
          }
        });
      }
    });
  } else if (brandMentions && Array.isArray(brandMentions) && brandMentions.length > 0) {
    uniqueBrands = brandMentions.map(b => ({ name: b, shortcode: null }));
  }
  const hasBrands = uniqueBrands.length > 0;
  const displayBrands = uniqueBrands.slice(0, 12);  // NOT: artik 12 "marka-reel cifti" (12 distinct marka degil)

  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
      <div className="flex flex-col md:grid md:grid-cols-[auto_auto_1fr] md:items-start gap-4 md:gap-6">
        {/* Col 1: Avatar 140px (wsrv.nl proxy + onError fallback preserved) */}
        {profilePic ? (
          <div className="shrink-0 w-[140px] h-[140px] rounded-full overflow-hidden border-2 border-yellow-400 shadow-lg bg-slate-800">
            {avatarFailed ? (
              <div className="w-full h-full flex items-center justify-center text-yellow-400 text-xs font-bold">
                {username?.substring(0, 2)?.toUpperCase() || '??'}
              </div>
            ) : (
              <img
                src={`https://wsrv.nl/?url=${encodeURIComponent(profilePic)}&w=300&h=300&fit=cover`}
                alt={username}
                className="w-full h-full object-cover"
                onError={() => setAvatarFailed(true)}
              />
            )}
          </div>
        ) : (
          <div className="shrink-0 w-[140px] h-[140px] rounded-full border-2 border-yellow-400 bg-slate-800 flex items-center justify-center text-4xl">
            👤
          </div>
        )}

        {/* Col 2: Username + Follower Count + Reel Cost (stacked) — 3j */}
        <div className="min-w-0 flex flex-col gap-3 md:self-start">
          <div>
            <p className="text-sm text-gray-400 mb-1">{t('results.username_label')}:</p>
            <p className={`font-bold text-yellow-200 whitespace-nowrap ${
              username?.length >= 23 ? 'text-[11px]' :
              username?.length >= 19 ? 'text-xs' :
              username?.length >= 16 ? 'text-sm' :
              username?.length >= 13 ? 'text-base' :
              'text-lg'
            }`}>
              {username && username !== 'Unknown' ? (
                <a
                  href={`https://www.instagram.com/${username}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-200 underline hover:text-yellow-100"
                >
                  @{username}
                </a>
              ) : (
                <>@{username}</>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">{t('results.followers_label')}:</p>
            <p className="text-xl font-bold text-gray-200">
              {formatFollowerCount(result?.follower_count)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-1">{t('results.reel_cost_label')}:</p>
            <p className="text-xl font-bold text-gray-200 whitespace-nowrap">
              {result?.payment_per_reel ? (
                <>
                  {formatCurrency(result.payment_per_reel)}{' '}
                  <span className="text-sm text-gray-400">{currency}</span>
                </>
              ) : '-'}
            </p>
          </div>
        </div>

        {/* Col 3: Partnerships + Brands (consolidated from removed Box 6) — 3j */}
        <div className="min-w-0 md:self-start flex flex-col gap-3">
          <div>
            <p className="text-sm text-gray-400">
              {t('results.branded_reel_count')}:{' '}
              <span className="text-xl font-bold text-gray-200 ml-1">{paidPartnerships}</span>
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-400 mb-2">{t('results.brands_section_title')}</p>
            {hasBrands ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {displayBrands.map((item, idx) => (
                  <div key={idx} className="min-w-0">
                    {item.shortcode ? (
                      <a
                        href={`https://www.instagram.com/reel/${item.shortcode}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-blue-400 hover:underline whitespace-nowrap truncate block"
                      >
                        {item.name || 'Unknown'}
                      </a>
                    ) : (
                      <p className="text-sm font-bold text-blue-400 m-0 whitespace-nowrap truncate">
                        {item.name || 'Unknown'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">{t('results.no_brand_partnerships')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Engagement Metrics Card
// v13.7: Full null-safety with optional chaining
// v14.5: Hidden likes support
// v15.6: Handbook error messages in red
const EngagementMetricsCard = ({ result, t, locale }) => {
  // v13.7 CRASH DEFENSE: Safe extraction with defaults
  const velocityEval = result?.velocity_evaluation || null;
  const visibilityEval = result?.visibility_evaluation || null;
  const engagementEval = result?.engagement_evaluation || null;
  const velocityExp = result?.velocity_explanation || null;
  const velocityUserExp = result?.velocity_user_explanation || null;
  const segmentDesc = result?.engagement_segment_description || null;
  const visibilitySegmentDesc = result?.visibility_segment_description || null;
  const visibilityAvgViews = result?.visibility_avg_views || result?.avg_reel_views || null;
  const followerInteraction = result?.follower_interaction || null;
  const engagementRate = result?.engagement_rate;
  // v14.5: Hidden likes detection
  const allLikesHidden = result?.all_likes_hidden || false;
  const hiddenLikesError = result?.hidden_likes_error || null;
  // COMMIT-E1: per-metrik bos-sebep anahtarlari + LONG metin helper.
  // reason_key yoksa (D2-oncesi eski kayit) -> null -> alt-metin BASILMAZ, cokme YOK.
  const visibilityReasonKey = result?.visibility_reason_key || null;
  const engagementReasonKey = result?.engagement_reason_key || null;
  // reason_key varsa onun LONG'u. YOKSA: like-tabanli metrik + all_likes_hidden ise eski-kayit sebebi BILINIYOR
  // (hidden_likes_long) -> D2-oncesi gizli-like raporlari sebep KAYBETMEZ. Izlenme-tabanli (gorunurluk) -> null.
  const reasonLong = (k, likeBased = false) => {
    if (k) return t(`results.${k}_long`);
    if (likeBased && allLikesHidden) return t('results.hidden_likes_long');
    return null;
  };
  // v15.6: Handbook error messages
  const followerInteractionError = result?.follower_interaction_error;
  const targetReelViewsError = result?.target_reel_views_error;
  // v31 gate: 90 gunde reel yok -> tutarli "Cannot be calculated"/"N/A". Commit 1 her iki mapper'dan gelir.
  const isInsufficient = result?.is_insufficient_data === true;

  const getColorClass = (evaluation) => {
    if (!evaluation) return 'text-white';
    if (evaluation.includes('🟢')) return 'text-green-400';
    if (evaluation.includes('🟡')) return 'text-yellow-400';
    if (evaluation.includes('🟠')) return 'text-orange-400';
    if (evaluation.includes('🔴')) return 'text-red-400';
    return 'text-white';
  };

  // Translate backend evaluation labels to current locale
  const translatedVelocity = velocityEval ? translateEval(velocityEval, locale) : null;
  const translatedVisibility = visibilityEval ? translateEval(visibilityEval, locale) : null;
  const translatedEngagement = engagementEval ? translateEval(engagementEval, locale) : null;
  const translatedVelocityExplanation = velocityExp ? translateFrequencyExplanation(velocityExp, locale) : null;
  const translatedVelocityUserExplanation = velocityUserExp ? translateFrequencyExplanation(velocityUserExp, locale) : null;
  const translatedSegmentDescription = segmentDesc ? translateFrequencyExplanation(segmentDesc, locale) : null;
  const translatedVisibilitySegmentDescription = visibilitySegmentDesc ? translateFrequencyExplanation(visibilitySegmentDesc, locale) : null;

  return (
    <>
      {/* Box A: Reel Activity */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
        {/* Reel Posting Activity */}
        <div>
          <p className="text-sm text-gray-400 mb-1">{t('results.reel_activity')}:</p>
          
          {isInsufficient ? (
            <div className="space-y-1">
              <p className={`text-xl font-bold ${velocityEval ? getColorClass(velocityEval) : 'text-red-400'}`}>
                {velocityEval ? translatedVelocity : `${t('results.cannot_be_calculated')} 🔴`}
              </p>
              <p className={`text-sm ${velocityEval ? getColorClass(velocityEval) : 'text-red-400'}`}>{t('results.no_reels_last_3_months')}</p>
            </div>
          ) : velocityEval ? (
            <div className="space-y-1">
              <p className={`text-xl font-bold ${getColorClass(velocityEval)}`}>
                {translatedVelocity}
              </p>
              {translatedVelocityExplanation && (
                <p className={`text-sm ${getColorClass(velocityEval)}`}>
                  {translatedVelocityExplanation}
                </p>
              )}
              {/* v31 dedupe: backend ayni metni 2 alana koyabilir -> ikinci satiri yalniz FARKLIYSA goster */}
              {translatedVelocityUserExplanation && translatedVelocityUserExplanation !== translatedVelocityExplanation && (
                <p className={`text-sm ${getColorClass(velocityEval)}`}>
                  {translatedVelocityUserExplanation}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xl font-bold text-yellow-400">{t('results.cannot_calculate')}</p>
          )}
        </div>
        
      </div>

      {/* Box V: Reel Visibility */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
        {/* v15.6: Görünürlük - title, badge, metric, then description (3d reorder) */}
        <div>
          <p className="text-sm text-gray-400 mb-1">{t('results.visibility_performance')}:</p>
          {isInsufficient ? (
            <>
              <p className={`text-xl font-bold ${visibilityEval ? getColorClass(visibilityEval) : 'text-red-400'}`}>
                {visibilityEval ? translatedVisibility : `${t('results.cannot_be_calculated')} 🔴`}
              </p>
              <p className={`text-sm mt-2 ${visibilityEval ? getColorClass(visibilityEval) : 'text-red-400'} opacity-70`}>{t('results.no_reels_last_3_months')}</p>
            </>
          ) : targetReelViewsError ? (
            <p className="text-sm font-bold text-red-500" data-testid="visibility-error">
              {targetReelViewsError}
            </p>
          ) : (
            <>
              <p className={`text-xl font-bold ${getColorClass(visibilityEval)}`}>
                {translatedVisibility || t('results.cannot_calculate')}
              </p>
              {visibilityAvgViews && (
                <div className={`mt-1 ${getColorClass(visibilityEval)}`}>
                  <p className="text-xs opacity-80 leading-snug">{t('results.avg_reel_views')}</p>
                  <p className="text-sm leading-snug">
                    <span className="text-xs opacity-80">{t('results.per_reel_suffix')}:</span>{' '}
                    <span className="font-bold">{formatNumberRuleD(visibilityAvgViews)}</span>
                  </p>
                </div>
              )}
              {translatedVisibilitySegmentDescription && (
                <BenchmarkLine
                  text={translatedVisibilitySegmentDescription}
                  className={`text-sm leading-snug mt-2 ${visibilityEval ? getColorClass(visibilityEval) : 'text-[#e5e5e5]'}`}
                />
              )}
              {/* COMMIT-E1: bos-gorunurluk sebep-slotu (izlenme-tabanli; floor_emptied/no_reels_90d LONG) */}
              {reasonLong(visibilityReasonKey) && (
                <p className="text-sm leading-snug mt-2 text-red-400 opacity-80" data-testid="visibility-reason">
                  {reasonLong(visibilityReasonKey)}
                </p>
              )}
            </>
          )}
        </div>

      </div>

      {/* Box E: Reel Engagement (per-reel + rate + evaluation) */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
        {/* 3f: Box V pattern — no flex wrapper, mb-1 for title->badge (4px), mt-3 between metric blocks. All VALUES use getColorClass(engagementEval). */}
        <p className="text-sm text-gray-400 mb-1">{t('results.engagement_section_title')}:</p>

        {/* Evaluation badge (right under title — 4px gap from title's mb-1) */}
        {isInsufficient ? (
          <p className="text-xl font-bold text-red-400">{t('results.na')}</p>
        ) : followerInteractionError ? (
          <p className="text-sm font-bold text-red-500">-</p>
        ) : allLikesHidden ? (
          <p className="text-sm font-bold text-red-500">-</p>
        ) : engagementEval ? (
          <p className={`text-xl font-bold ${getColorClass(engagementEval)}`}>
            {translatedEngagement}
          </p>
        ) : (
          <p className="text-xl font-bold text-yellow-400">-</p>
        )}

        {/* 3g: engagement_per_reel — 2-satir: label ust + deger alt (gorunurluk ile simetrik) */}
        <div className={`leading-snug mt-3 ${engagementEval ? getColorClass(engagementEval) : 'text-[#e5e5e5]'}`}>
          <p className="text-xs opacity-80 leading-snug">{t('results.engagement_per_reel')}</p>
          <p className="text-sm leading-snug">
            <span className="text-xs opacity-80">{t('results.per_reel_suffix')}:</span>{' '}
            {isInsufficient ? (
              <span className="font-bold text-red-400">{t('results.na')}</span>
            ) : followerInteractionError ? (
              <span className="font-bold text-red-500" data-testid="engagement-per-reel-error">{followerInteractionError}</span>
            ) : allLikesHidden ? (
              <span className="font-bold text-red-500" data-testid="engagement-per-reel-cannot">{t('results.cannot_be_calculated')}</span>
            ) : (
              <span className="font-bold">{followerInteraction && followerInteraction > 0 ? formatNumberRuleD(followerInteraction) : '-'}</span>
            )}
          </p>
        </div>

        {/* 3g: engagement_rate — label + value both in eval color */}
        <p className={`text-sm leading-snug mt-3 ${engagementEval ? getColorClass(engagementEval) : 'text-[#e5e5e5]'}`}>
          {t('results.engagement_rate')}:{' '}
          {isInsufficient ? (
            <span className="text-sm font-bold text-red-400">{t('results.na')}</span>
          ) : followerInteractionError ? (
            <span className="text-sm font-bold text-red-500" data-testid="engagement-rate-error">{followerInteractionError}</span>
          ) : allLikesHidden ? (
            <span className="text-sm font-bold text-red-500" data-testid="engagement-rate-cannot">{t('results.cannot_be_calculated')}</span>
          ) : engagementRate != null && engagementRate !== 0 ? (
            <span className="text-sm font-bold">
              {formatPercent(typeof engagementRate === 'number' ? engagementRate : parseFloat(String(engagementRate).replace(',', '.')))}
            </span>
          ) : (
            <span className="text-sm font-bold text-yellow-400">-</span>
          )}
        </p>

        {/* 3g: Segment description — single inline line, label + value both in eval color */}
        {translatedSegmentDescription && (
          <BenchmarkLine
            text={translatedSegmentDescription}
            className={`text-sm leading-snug mt-3 ${engagementEval ? getColorClass(engagementEval) : 'text-[#e5e5e5]'}`}
          />
        )}
        {/* COMMIT-E1: bos-etkilesim sebep-slotu (hidden_likes/floor_emptied/no_reels_90d LONG; like-tabanli -> eski-kayit fallback) */}
        {reasonLong(engagementReasonKey, true) && (
          <p className="text-sm leading-snug mt-3 text-red-400 opacity-80" data-testid="engagement-reason">
            {reasonLong(engagementReasonKey, true)}
          </p>
        )}
      </div>
    </>
  );
};

// Sub-component: Brand Partnerships Card
// v13.7: Full null-safety with optional chaining
const BrandPartnershipsCard = ({ result, t }) => {
  // v13.7 CRASH DEFENSE: Safe extraction with defaults
  const paidPartnerships = result?.paid_partnerships_count || 0;
  const collaborations = result?.collaborations_by_reel;
  const brandMentions = result?.brand_mentions;

  return (
    <>
      {/* Left card: compact partnership count */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
        <p className="text-sm text-gray-400 mb-1">{t('results.branded_reel_count')}:</p>
        <p className="text-2xl font-bold text-yellow-400">
          {paidPartnerships}
        </p>
      </div>

      {/* Right card: brands grid (max 4 rows x 3 cols = 12 items) */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
        <p className="text-sm text-gray-400 mb-1">{t('results.brands_section_title')}</p>
          {collaborations && Array.isArray(collaborations) && collaborations.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 flex-1">
              {(() => {
                // Her marka için ilk tespit edilen reel'in shortcode'unu bul
                const brandFirstShortcode = {};
                collaborations.forEach(collab => {
                  if (collab?.brands && Array.isArray(collab.brands)) {
                    collab.brands.forEach(brand => {
                      const key = brand?.toLowerCase();
                      if (key && !brandFirstShortcode[key] && collab.shortcode) {
                        brandFirstShortcode[key] = collab.shortcode;
                      }
                    });
                  }
                });
                // Unique marka listesi (sıra korunarak)
                const seen = new Set();
                const uniqueBrands = [];
                collaborations.forEach(collab => {
                  if (collab?.brands && Array.isArray(collab.brands)) {
                    collab.brands.forEach(brand => {
                      const key = brand?.toLowerCase();
                      if (key && !seen.has(key)) {
                        seen.add(key);
                        uniqueBrands.push({ name: brand, shortcode: brandFirstShortcode[key] });
                      }
                    });
                  }
                });
                return uniqueBrands.slice(0, 12).map((item, idx) => (
                  <div key={idx}>
                    {item.shortcode ? (
                      <a
                        href={`https://www.instagram.com/reel/${item.shortcode}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-blue-400 hover:underline whitespace-nowrap m-0"
                      >
                        {item.name || 'Unknown'}
                      </a>
                    ) : (
                      <p className="text-sm font-bold text-blue-400 m-0 whitespace-nowrap">
                        {item.name || 'Unknown'}
                      </p>
                    )}
                  </div>
                ));
              })()}
            </div>
          ) : brandMentions && Array.isArray(brandMentions) && brandMentions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 flex-1">
              {brandMentions.slice(0, 12).map((brand, idx) => (
                <pre key={idx} className="text-sm font-bold text-blue-400 whitespace-nowrap m-0 font-mono">
                  {brand || 'Unknown'}
                </pre>
              ))}
            </div>
        ) : (
          <p className="text-2xl font-bold text-yellow-400">-</p>
        )}
      </div>
    </>
  );
};

// Sub-component: Cost Analysis Cards
// v13.7: Full null-safety with optional chaining
// v14.5: Hidden likes support
// v15.6: Handbook error messages in red
const CostAnalysisSection = ({ result, t, locale, currency }) => {
  // v13.7 CRASH DEFENSE: Safe extraction with defaults
  const paymentPerReel = result?.payment_per_reel;
  const costPerView = result?.cost_per_view;
  const targetReelViews = result?.target_reel_views;
  const targetReelViewsRangeDisplay = result?.target_reel_views_range_display;
  const totalEngagement = result?.total_engagement;
  const expectedInteractionRate = result?.expected_interaction_rate;
  const storyViewMin = result?.story_view_min;
  const storyViewMax = result?.story_view_max;
  const storyViewRange = result?.story_view_range;
  const storyViewAverage = result?.story_view_average;
  const linkClickRange = result?.link_click_range;
  const linkClickAverage = calculateAverageFromRange(result?.link_click_range);
  // v14.5: Hidden likes detection
  const allLikesHidden = result?.all_likes_hidden || false;
  // v15.6: Handbook error messages
  const targetReelViewsError = result?.target_reel_views_error;
  const totalEngagementError = result?.total_engagement_error;
  // COMMIT-E1: forecast-etkilesim bos-sebep anahtari + LONG helper (reason_key yoksa null -> alt-metin YOK, cokme YOK).
  // NOT: forecast_views (izlenme) like-tabanli DEGIL, likes_hidden fallback'i yoktu -> sebep-slotu bu commit'te YOK (E1 kapsami=6 fallback).
  const forecastEngagementReasonKey = result?.forecast_engagement_reason_key || null;
  // COMMIT-E1c AMEND: story izlenme/link avg_views'e dayanir (predict_story_views_v15 :4499 avg_views None -> None).
  // avg_views bos-sebebi = visibility_reason_key (floor_emptied/no_reels_90d) -> story bos-sebebi de AYNI (bagimsiz degil).
  const visibilityReasonKey = result?.visibility_reason_key || null;
  // reason_key varsa LONG'u; YOKSA like-tabanli + all_likes_hidden ise eski-kayit sebebi (hidden_likes_long). Izlenme -> null.
  const reasonLong = (k, likeBased = false) => {
    if (k) return t(`results.${k}_long`);
    if (likeBased && allLikesHidden) return t('results.hidden_likes_long');
    return null;
  };
  // v31 gate + NaN-guard: 90 gunde reel yok -> "N/A"; ham "NaN" string'i ASLA ekrana basma.
  const isInsufficient = result?.is_insufficient_data === true;
  const safeRangeDisplay = (targetReelViewsRangeDisplay && !String(targetReelViewsRangeDisplay).includes('NaN'))
    ? targetReelViewsRangeDisplay : null;
  
  return (
    <>
      {/* Section Header */}
      <div className="mb-4 mt-6">
        <h3 className="text-2xl font-bold text-gray-300">{t('results.collaboration_section_title')}</h3>
        <p className="text-xs text-gray-400 mt-1">{t('results.collaboration_section_desc')}</p>
        <p className="text-xs text-gray-400">{t('results.collaboration_section_note')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Box 8: Forecasted Reel Views + CPV (embedded) */}
        <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
          <div className="flex flex-col gap-3">
            {/* Expected Views */}
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('results.expected_views_label')}:</p>
              {isInsufficient ? (
                <>
                  <p className="text-xl font-bold text-red-400">{t('results.cannot_be_calculated')} 🔴</p>
                  <p className="text-xs text-red-400 mt-1">{t('results.forecast_no_reels_basis')}</p>
                </>
              ) : targetReelViewsError ? (
                <p className="text-sm font-bold text-red-500" data-testid="expected-views-error">
                  {targetReelViewsError}
                </p>
              ) : (
                <>
                  <p className="text-2xl font-bold text-gray-200">
                    {safeRangeDisplay ||
                      (targetReelViews ? formatNumberRuleD(targetReelViews) : '-')}
                  </p>
                  {targetReelViews && (
                    <p className="text-xs text-gray-200 mt-1">
                      {t('results.expected_views_avg')}: {formatNumberRuleD(targetReelViews)}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* CPV — embedded with divider, only when both payment & costPerView present */}
            {paymentPerReel && costPerView && (
              <>
                <hr className="border-white/10 my-1" />
                <div>
                  <p className="text-sm text-gray-400 mb-1">{t('results.cost_per_view')}:</p>
                  <p className="text-2xl font-bold text-gray-200">
                    {formatDecimal(costPerView, 2)} {currency}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Box 9: Forecasted Reel Engagement + Rate */}
        <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
          <div className="flex flex-col gap-3">
            {/* Total Engagement */}
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('results.total_engagement')}:</p>
              {isInsufficient ? (
                <p className="text-2xl font-bold text-red-400">{t('results.na')}</p>
              ) : totalEngagementError ? (
                <p className="text-sm font-bold text-red-500" data-testid="total-engagement-error">
                  {totalEngagementError}
                </p>
              ) : allLikesHidden ? (
                <p className="text-sm font-bold text-red-500" data-testid="total-engagement-cannot">
                  {t('results.cannot_be_calculated')}
                </p>
              ) : totalEngagement ? (
                <>
                  <p className="text-2xl font-bold text-gray-200">
                    {formatRange(
                      Math.round(totalEngagement * 0.8),
                      Math.round(totalEngagement * 1.2)
                    )}
                  </p>
                  <p className="text-xs text-gray-200 mt-1">
                    {t('results.expected_views_avg')}: {formatNumberRuleD(totalEngagement)}
                  </p>
                </>
              ) : (
                <p className="text-sm font-bold text-red-500" data-testid="engagement-insufficient-data">
                  {totalEngagementError || t('results.cannot_be_calculated')}
                </p>
              )}
              {/* COMMIT-E1: forecast-etkilesim bos-sebep (SEPET-2/like-tabanli LONG) */}
              {reasonLong(forecastEngagementReasonKey, true) && (
                <p className="text-xs leading-snug mt-1 text-red-400 opacity-80" data-testid="forecast-eng-reason">
                  {reasonLong(forecastEngagementReasonKey, true)}
                </p>
              )}
            </div>

            <hr className="border-white/10 my-1" />

            {/* Expected Engagement Rate */}
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('results.engagement_rate_expected')}:</p>
              {isInsufficient ? (
                <p className="text-2xl font-bold text-red-400">{t('results.na')}</p>
              ) : totalEngagementError ? (
                <p className="text-sm font-bold text-red-500" data-testid="expected-rate-error">
                  {totalEngagementError}
                </p>
              ) : allLikesHidden ? (
                <p className="text-sm font-bold text-red-500" data-testid="expected-rate-cannot">
                  {t('results.cannot_be_calculated')}
                </p>
              ) : (
                <p className="text-2xl font-bold text-gray-200">
                  {expectedInteractionRate
                    ? formatPercent(typeof expectedInteractionRate === 'number' ? expectedInteractionRate : parseFloat(String(expectedInteractionRate).replace(',', '.')))
                    : <span className="text-sm font-bold text-red-500">{totalEngagementError || t('results.cannot_be_calculated')}</span>}
                </p>
              )}
              {/* COMMIT-E1: forecast-ER bos-sebep (SEPET-2/like-tabanli LONG) */}
              {reasonLong(forecastEngagementReasonKey, true) && (
                <p className="text-xs leading-snug mt-1 text-red-400 opacity-80" data-testid="forecast-er-reason">
                  {reasonLong(forecastEngagementReasonKey, true)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Box 10+11 (3i+3m): Story Views + Story Link Clicks merged into one card (Box 8/9 pattern) */}
        <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4">
          <div className="flex flex-col gap-3">
            {/* Top half: Story Views */}
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('results.story_views_label')}:</p>
              {isInsufficient ? (
                <p className="text-2xl font-bold text-red-400">{t('results.na')}</p>
              ) : (storyViewMin && storyViewMax) ? (
                <p className="text-2xl font-bold text-gray-200">{formatRange(storyViewMin, storyViewMax)}</p>
              ) : (storyViewRange && /\d/.test(String(storyViewRange))) ? (
                <p className="text-2xl font-bold text-gray-200">{storyViewRange}</p>
              ) : (
                /* COMMIT-E1c: bos/Hesaplanamadi -> Reel kutusuyla AYNI kucuk-kirmizi stil (buyuk-deger slot'undan cikar) */
                <>
                  <p className="text-sm font-bold text-red-500">{t('results.cannot_be_calculated')}</p>
                  {/* COMMIT-E1c AMEND: sebep = visibility_reason_key (story avg_views'e dayanir) - Reel deseni */}
                  {reasonLong(visibilityReasonKey) && (
                    <p className="text-sm leading-snug mt-2 text-red-400 opacity-80">{reasonLong(visibilityReasonKey)}</p>
                  )}
                </>
              )}
              {!isInsufficient && storyViewAverage && (
                <p className="text-xs text-gray-200 mt-1">
                  {t('results.expected_views_avg')}: {formatNumberRuleD(storyViewAverage)}
                </p>
              )}
            </div>

            <hr className="border-white/10 my-1" />

            {/* Bottom half: Story Link Clicks */}
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('results.link_clicks_label')}:</p>
              {isInsufficient ? (
                <p className="text-2xl font-bold text-red-400">{t('results.na')}</p>
              ) : (linkClickRange && /\d/.test(String(linkClickRange))) ? (
                <p className="text-2xl font-bold text-gray-200">{linkClickRange}</p>
              ) : (
                /* COMMIT-E1c: bos/Hesaplanamadi -> Reel kutusuyla AYNI kucuk-kirmizi stil */
                <>
                  <p className="text-sm font-bold text-red-500">{t('results.cannot_be_calculated')}</p>
                  {/* COMMIT-E1c AMEND: sebep = visibility_reason_key (link, story_view->avg_views'e dayanir) - Reel deseni */}
                  {reasonLong(visibilityReasonKey) && (
                    <p className="text-sm leading-snug mt-2 text-red-400 opacity-80">{reasonLong(visibilityReasonKey)}</p>
                  )}
                </>
              )}
              {!isInsufficient && linkClickAverage !== null && (
                <p className="text-xs text-gray-200 mt-1">
                  {t('results.expected_views_avg')}: {formatNumberRuleD(linkClickAverage)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Warning: Missing payment */}
      {!paymentPerReel && (
        <div className="text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-6">
          <p className="font-semibold">
            {t('results.cost_warning')}
          </p>
        </div>
      )}
    </>
  );
};

// v13.7: Safe number parser - strips commas/dots and handles all edge cases
const safeParseNumber = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  if (typeof val === 'string') {
    // Remove commas, dots used as thousand separators, and whitespace
    const cleaned = val.replace(/,/g, '').replace(/\s/g, '').trim();
    // Handle K/M suffixes
    if (cleaned.endsWith('K') || cleaned.endsWith('k')) {
      return parseFloat(cleaned.replace(/[kK]/, '')) * 1000 || 0;
    }
    if (cleaned.endsWith('M') || cleaned.endsWith('m')) {
      return parseFloat(cleaned.replace(/[mM]/, '')) * 1000000 || 0;
    }
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Main Component
const AnalysisResults = ({ result, currency }) => {
  // Hook must be called unconditionally at the top
  const { t, locale, isRTL } = useTranslation();
  // Currency etiketi: prop (stored/secili) varsa onu, yoksa locale-default (hardcoded TRY DEGIL). Donusum YOK.
  const displayCurrency = currency || getDefaultCurrency(locale);

  // v13.7 CRASH DEFENSE: Early null check
  if (!result) return null;
  
  // v13.7: Ensure result has at minimum a username
  if (!result.username && !result.profile_pic_url) return null;

  // PDF Download function using html2canvas + jspdf (Client-Side Only)
  // v7.2: Print-friendly format - ALL white backgrounds, dark text, keep indicator colors
  const handleDownloadPDF = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      
      const element = document.getElementById('influencer-analysis-section');
      const pdfButton = document.getElementById('pdf-download-btn');
      const pdfLogo = document.getElementById('pdf-logo');
      const logoImg = document.getElementById('pdf-logo-img');
      
      if (!element) return;
      
      // Hide button, show logo for PDF
      if (pdfButton) pdfButton.style.display = 'none';
      if (pdfLogo) pdfLogo.style.display = 'flex';
      
      // v7.2: Store original styles for restoration
      const originalStyles = new Map();
      
      // v7.2: Apply print-friendly styles - ALL backgrounds white, dark text
      const applyPrintStyles = () => {
        // Get ALL elements inside the section
        const allElements = element.querySelectorAll('*');
        
        allElements.forEach(el => {
          const computedStyle = window.getComputedStyle(el);
          const bgColor = computedStyle.backgroundColor;
          const textColor = computedStyle.color;
          
          // Store original inline style
          if (!originalStyles.has(el)) {
            originalStyles.set(el, el.style.cssText);
          }
          
          // v7.2: Make ALL backgrounds white (except completely transparent)
          if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
            el.style.setProperty('background-color', '#ffffff', 'important');
            el.style.setProperty('background', '#ffffff', 'important');
          }
          
          // v7.2: Handle text colors for contrast on white background
          if (textColor) {
            const rgb = textColor.match(/\d+/g);
            if (rgb && rgb.length >= 3) {
              const r = parseInt(rgb[0]);
              const g = parseInt(rgb[1]);
              const b = parseInt(rgb[2]);
              
              // Check if it's a status indicator color (red, green, yellow/orange)
              const isRed = r > 180 && g < 100 && b < 100;
              const isGreen = g > 150 && r < 150 && b < 150;
              const isYellow = r > 200 && g > 150 && b < 100;
              const isOrange = r > 200 && g > 100 && g < 180 && b < 100;
              
              // Keep indicator colors but make them slightly darker for print
              if (isRed) {
                el.style.setProperty('color', '#DC2626', 'important'); // Darker red
              } else if (isGreen) {
                el.style.setProperty('color', '#059669', 'important'); // Darker green
              } else if (isYellow || isOrange) {
                el.style.setProperty('color', '#D97706', 'important'); // Darker orange/yellow
              } else {
                // All other text: make it very dark for contrast
                // White or light text -> black
                if ((r + g + b) / 3 > 150) {
                  el.style.setProperty('color', '#1a1a1a', 'important');
                }
                // Gray text -> dark gray
                else if (r === g && g === b && r > 80) {
                  el.style.setProperty('color', '#333333', 'important');
                }
                // Already dark text -> keep or make slightly darker
                else if ((r + g + b) / 3 > 50) {
                  el.style.setProperty('color', '#1a1a1a', 'important');
                }
              }
            }
          }
        });
        
        // Set main element background white
        element.style.setProperty('background-color', '#ffffff', 'important');
        element.style.setProperty('background', '#ffffff', 'important');
        
        // v7.2: Also force all borders to be visible (light gray)
        allElements.forEach(el => {
          const computedStyle = window.getComputedStyle(el);
          const borderColor = computedStyle.borderColor;
          if (borderColor && borderColor !== 'transparent') {
            el.style.setProperty('border-color', '#e5e5e5', 'important');
          }
        });
      };
      
      // v7.2: Restore original styles after PDF generation
      const restoreStyles = () => {
        originalStyles.forEach((style, el) => {
          el.style.cssText = style;
        });
        // Reset main element
        element.style.cssText = originalStyles.get(element) || '';
      };
      
      // Store main element style
      originalStyles.set(element, element.style.cssText);
      
      // Apply print-friendly styles
      applyPrintStyles();
      
      // Wait for logo image to load
      if (logoImg && !logoImg.complete) {
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          logoImg.onerror = resolve;
          setTimeout(resolve, 1000); // Fallback timeout
        });
      }
      
      // Longer delay to ensure DOM updates are rendered
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Generate canvas from element with white background
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff', // v7.2: White background for print
        logging: false
      });
      
      // Restore original styles
      restoreStyles();
      
      // Restore button, hide logo
      if (pdfButton) pdfButton.style.display = 'flex';
      if (pdfLogo) pdfLogo.style.display = 'none';
      
      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      
      // v7.1: Calculate PDF dimensions with narrow margins (10mm each side)
      const marginLeft = 10;
      const marginRight = 10;
      const marginTop = 10;
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const contentWidth = pageWidth - marginLeft - marginRight; // 190mm usable width
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = marginTop;
      
      // Add image to PDF with narrow margins
      pdf.addImage(imgData, 'JPEG', marginLeft, position, contentWidth, imgHeight);
      heightLeft -= (pageHeight - marginTop * 2);
      
      while (heightLeft > 0) {
        position = marginTop - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', marginLeft, position, contentWidth, imgHeight);
        heightLeft -= (pageHeight - marginTop * 2);
      }
      
      // Generate filename: OPARA_Rapor_{username}_{date}.pdf
      const today = new Date().toISOString().split('T')[0];
      const filename = `OPARA_Rapor_${result.username || 'analiz'}_${today}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(t('results.pdf_error'));
    }
  };

  return (
    <div id="influencer-analysis-section" data-testid="analysis-results" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="bg-gray-900 border-2 border-[#FFDD59] mb-6">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6">
          <CardTitle className="text-lg sm:text-2xl text-yellow-200">
            {t('results.section_title')}
          </CardTitle>
          
          {/* PDF Download Button - Hidden in PDF output */}
          <Button
            id="pdf-download-btn"
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            className="bg-gray-200 border-gray-300 text-red-600 hover:bg-gray-300 hover:text-red-700 font-medium text-xs sm:text-sm"
            data-testid="download-pdf-btn"
          >
            <FileDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{t('results.download_pdf')}</span>
            <span className="sm:hidden">PDF</span>
          </Button>
          
          {/* OPARA Logo - Only visible in PDF output */}
          <div 
            id="pdf-logo" 
            className="items-center gap-2"
            style={{ display: 'none' }}
          >
            <img 
              id="pdf-logo-img"
              src="/opara-logo-trimmed.png" 
              alt="OPARA" 
              className="h-10"
              style={{ height: '40px', objectFit: 'contain' }}
            />
          </div>
          
          {/* Preload logo image (hidden) */}
          <img 
            src="/opara-logo-trimmed.png" 
            alt="" 
            style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
          />
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          {/* Row 1: Influencer Info (full width) — now also hosts partnerships + brands (3j) */}
          <div className="mb-3 sm:mb-4">
            <ProfileInfoCard result={result} t={t} currency={displayCurrency} />
          </div>

          {/* 3j: Section heading above current-performance metrics */}
          <h3 className="text-2xl font-bold text-gray-300 mt-6 mb-3">
            {t('results.current_performance_section_title')}
          </h3>

          {/* Row 2: Engagement metrics — 3 cards (activity, visibility, engagement) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
            <EngagementMetricsCard result={result} t={t} locale={locale} />
          </div>

          {/* Row 3 (Brand Partnerships) removed in 3j — consolidated into ProfileInfoCard Col 3 */}

          {/* Cost Analysis Section */}
          <CostAnalysisSection result={result} t={t} locale={locale} currency={displayCurrency} />
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalysisResults;
